import { useEffect, useMemo, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import StatCard from "@/components/StatCard";
import { currency, fmtDate, COMMISSION_STATUSES } from "@/lib/format";
import { DollarSign, Clock, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Commissions() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/commissions"); setItems(data); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/commissions/${id}`, { status }); load(); toast.success("Updated"); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return items.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (s && ![c.patient_name, c.partner_name, c.partner_organization].some((x) => (x || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [items, search, filter]);

  const totals = useMemo(() => ({
    total: items.reduce((s, x) => s + (x.commission_amount || 0), 0),
    paid: items.filter((x) => x.status === "paid").reduce((s, x) => s + x.commission_amount, 0),
    pending: items.filter((x) => x.status !== "paid").reduce((s, x) => s + x.commission_amount, 0),
  }), [items]);

  return (
    <div data-testid="admin-commissions-page">
      <PageHeader eyebrow="Finance" title="Commissions"
        subtitle="All partner commissions across the network. Approve and mark paid." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <StatCard label="Total earned by partners" value={currency(totals.total)} icon={DollarSign} testid="stat-total" />
        <StatCard label="Pending payout" value={currency(totals.pending)} icon={Clock} accent="#B26B00" testid="stat-pending" />
        <StatCard label="Paid out" value={currency(totals.paid)} icon={CheckCircle2} accent="#2A4D3E" testid="stat-paid" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9992]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by patient or partner…"
            data-testid="commissions-search"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E8E6E1] rounded-full bg-white text-sm focus:outline-none focus:border-[#2A4D3E]" />
        </div>
        <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1">
          {["all", ...COMMISSION_STATUSES.map((s) => s.id)].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`comm-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition ${filter === f ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Partner</th>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Treatment value</th>
                <th className="px-5 py-3">Rate</th>
                <th className="px-5 py-3">Commission</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F7F5F2]" data-testid={`comm-${c.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-[#2A332C]">{c.partner_name}</div>
                    <div className="text-[11px] text-[#8E9992]">{c.partner_organization}</div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{c.patient_name}</td>
                  <td className="px-5 py-3.5 text-sm text-[#5C6660]">{currency(c.treatment_value)}</td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{(c.commission_rate * 100).toFixed(0)}%</td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-[#2A4D3E]">{currency(c.commission_amount)}</td>
                  <td className="px-5 py-3.5">
                    <select value={c.status} onChange={(e) => updateStatus(c.id, e.target.value)}
                      data-testid={`comm-status-${c.id}`}
                      className="text-[11px] uppercase font-semibold tracking-wider border border-[#E8E6E1] rounded-full px-3 py-1 bg-white focus:outline-none">
                      {COMMISSION_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-[#8E9992]">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-12 text-center text-sm text-[#8E9992]">No commissions match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
